// ============================================================
// Moteur PUR du FORFAIT ANNUEL EN JOURS — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-forfait.spec.ts.
// ============================================================
//
// « Combien de jours de repos me reste-t-il ? »
//
// Le forfait jours (L3121-58) ne compte pas des heures, il compte des JOURNÉES. Le salarié doit
// travailler au plus `forfait_days` jours dans l'année — 218 par défaut, journée de solidarité
// comprise (L3121-64). Comme l'année offre plus de jours travaillables que le forfait n'en
// autorise, la différence lui revient en JOURS DE REPOS. C'est l'équivalent, pour lui, des JRTT
// d'un salarié à 39 h : la même idée, un décompte différent.
//
// LE CALCUL, en une ligne :
//
//   jours de repos dus = (jours travaillables de l'année) − forfait
//
// où « jours travaillables » = les jours calendaires, moins les repos hebdomadaires, moins les
// congés payés dus, moins les fériés chômés qui tombent un jour ouvré. Le résultat varie d'une
// année sur l'autre (un 14 juillet un dimanche ne rend pas de jour), d'où un calcul et non une
// constante : en 2026 le compte tombe à 10 jours, en 2027 à 9. Se tromper d'un jour, c'est une
// journée de travail donnée ou volée.
//
// CE QUE CE MOTEUR NE FAIT PAS : il ne décompte aucune heure. Un forfait jours qui afficherait
// « +3 h cette semaine » serait un contresens — et, devant un juge, l'indice que le forfait
// n'était pas appliqué. Les heures restent visibles au planning, elles ne se totalisent nulle part
// (cf. `countsHours` dans hr-worktime.ts).

import { addDays, isoWeekdayOf } from './hr-schedule-engine';

// Ce qu'il faut pour trancher, sans rien deviner. Les jours travaillés viennent du PLANNING
// (le réalisé), pas d'une projection : c'est ce qui a réellement eu lieu qui consomme le forfait.
export interface ForfaitInput {
	forfait_days: number;						// plafond annuel du contrat (218 par défaut)
	from: string;										// période observée « YYYY-MM-DD » — l'année civile en pratique
	to: string;
	worked_dats: string[];					// jours DISTINCTS effectivement travaillés (planning, absences déduites)
	rest_weekdays: number[];				// repos hebdomadaires en jours ISO (1..7) — samedi/dimanche par défaut
	closed_holiday_dats: string[];	// fériés CHÔMÉS de la période (établissement fermé)
	leave_days_due: number;					// congés payés DUS sur l'année, en jours ouvrés (25 en général)
	rest_days_taken: number;				// jours de repos « forfait » déjà posés et approuvés
}

export interface ForfaitReport {
	forfait_days: number;
	workable_days: number;					// jours ouvrés de la période, fériés chômés déduits
	worked_days: number;						// … dont réellement travaillés
	rest_days_due: number;					// jours de repos que l'année doit au salarié
	rest_days_taken: number;
	rest_days_left: number;					// … qu'il lui reste à poser
	remaining_workable: number;			// jours qu'il peut encore travailler avant d'atteindre le forfait
	over_forfait: number;						// jours travaillés AU-DELÀ du forfait (0 tant qu'on est dans les clous)
	issues: string[];
}

// Jours OUVRÉS d'une période : ni repos hebdomadaire, ni férié chômé. C'est la base de tout le
// reste — et la raison pour laquelle le nombre de jours de repos change chaque année.
export function workableDaysIn(
	from: string, to: string, restWeekdays: number[], closedHolidayDats: string[] = [],
): number {
	if (!from || !to || to < from) return 0;
	const rest = new Set(restWeekdays.length ? restWeekdays : [6, 7]);
	const closed = new Set(closedHolidayDats);
	let n = 0;
	for (let d = from; d <= to; d = addDays(d, 1)) {
		if (rest.has(isoWeekdayOf(d))) continue;		// un férié tombant un repos ne rend rien
		if (closed.has(d)) continue;
		n++;
	}
	return n;
}

export function buildForfaitReport(input: ForfaitInput): ForfaitReport {
	const forfait = Math.max(1, Math.round(input.forfait_days));
	const rest = input.rest_weekdays?.length ? input.rest_weekdays : [6, 7];
	const workable = workableDaysIn(input.from, input.to, rest, input.closed_holiday_dats ?? []);

	// Seuls les jours ouvrés comptent : une journée exceptionnellement travaillée un dimanche ne
	// consomme pas le forfait au même titre (elle relève du repos hebdomadaire, pas du décompte).
	const restSet = new Set(rest);
	const closedSet = new Set(input.closed_holiday_dats ?? []);
	const worked = new Set(
		(input.worked_dats ?? []).filter(d => d >= input.from && d <= input.to
			&& !restSet.has(isoWeekdayOf(d)) && !closedSet.has(d)),
	).size;

	// LE calcul : ce que l'année laisse au-delà du forfait revient en repos. Les congés payés dus
	// occupent la place AVANT le forfait — ils ne sont pas des jours travaillables.
	const leaveDue = Math.max(0, input.leave_days_due ?? 0);
	const restDue = Math.max(0, workable - leaveDue - forfait);
	const taken = Math.max(0, input.rest_days_taken ?? 0);

	const overForfait = Math.max(0, worked - forfait);
	const issues: string[] = [];
	if (overForfait > 0) {
		issues.push(labelDays(overForfait) + ' au-delà du forfait de ' + labelDays(forfait)
			+ ' : le dépassement doit être compensé par du repos, ou faire l\'objet d\'un avenant de renonciation rémunéré (L3121-59).');
	}
	if (taken > restDue) {
		issues.push(labelDays(taken - restDue) + ' de repos de plus que ce que l\'année ouvre ('
			+ labelDays(restDue) + ') : le forfait sera dépassé à l\'envers, vérifiez les jours posés.');
	}

	return {
		forfait_days: forfait,
		workable_days: workable,
		worked_days: worked,
		rest_days_due: restDue,
		rest_days_taken: taken,
		rest_days_left: restDue - taken,
		remaining_workable: forfait - worked,
		over_forfait: overForfait,
		issues,
	};
}

function labelDays(n: number): string {
	const txt = Number.isInteger(n) ? ('' + n) : ('' + n).replace('.', ',');
	return txt + (n >= 2 ? ' jours' : ' jour');
}
